import api from './api';

export interface IndicatorResult {
  indicatorId: number;
  indicatorName: string;
  score: number;
  conclusionTitle: string | null;
  conclusionDescription: string | null;
  level: string | null;
}

export interface AnswerDetail {
  questionId: number;
  questionTitle: string;
  questionType: string;
  answerText: string;
  questionSettings?: string | null;
  answerRaw?: string | null;
  score: number | null;
}

export interface QuestionnaireAnswer {
  id: number;
  userId: number;
  userName: string;
  studentNumber: string | null;
  schoolName: string | null;
  className: string | null;
  grade: string | null;
  /** 答卷创建时间（≈开始作答） */
  createdAt?: string | null;
  submittedAt: string;
  /** 答题时长（秒）= submittedAt − createdAt。无法计算时为 null。 */
  durationSeconds?: number | null;
  /** COMPLETED / IN_PROGRESS / PENDING — 从 user_questionnaires.status 取 */
  completionStatus?: 'COMPLETED' | 'IN_PROGRESS' | 'PENDING' | null;
  /** 答卷是否有效。false = 已作废，不计入统计/指标/导出。 */
  valid: boolean;
  /** 作废原因（仅 valid=false 时有值） */
  invalidReason?: string | null;
  /** 作废时间（仅 valid=false 时有值） */
  invalidatedAt?: string | null;
  answerDetails: AnswerDetail[];
  indicatorResults: IndicatorResult[];
}

export interface AnswerPage {
  content: QuestionnaireAnswer[];
  totalElements: number;
  totalPages: number;
  number: number;        // 当前页（0-based）
  size: number;
}

export type AnswerStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

/** 答题时长筛选区间（与后端 DurationBucket 枚举一一对应） */
export type DurationBucket =
  | 'M0_5' | 'M5_10' | 'M10_20' | 'M20_30'
  | 'M30_40' | 'M40_50' | 'M50_60' | 'M60_PLUS';

/** 时长区间下拉选项（label 给用户看，value 传后端） */
export const DURATION_BUCKETS: { value: DurationBucket; label: string }[] = [
  { value: 'M0_5',    label: '0-5 分钟' },
  { value: 'M5_10',   label: '5-10 分钟' },
  { value: 'M10_20',  label: '10-20 分钟' },
  { value: 'M20_30',  label: '20-30 分钟' },
  { value: 'M30_40',  label: '30-40 分钟' },
  { value: 'M40_50',  label: '40-50 分钟' },
  { value: 'M50_60',  label: '50-60 分钟' },
  { value: 'M60_PLUS', label: '60 分钟以上' },
];

export const answerService = {
  /**
   * 获取问卷答题结果（分页 + 可选 status / schoolName / className / 时长区间 过滤）。size 默认 100。
   * schoolName / className 走 answer.school_name_snapshot / class_name_snapshot 字段（只看新数据）。
   * durationBucket 走 submitted_at − created_at，下推 SQL 筛选（分页/总数正确）。
   */
  getQuestionnaireAnswers: async (
    questionnaireId: number,
    page: number = 0,
    size: number = 100,
    status?: AnswerStatus,
    schoolName?: string,
    className?: string,
    durationBucket?: DurationBucket,
  ): Promise<AnswerPage> => {
    const params: Record<string, any> = { page, size };
    if (status) params.status = status;
    if (schoolName) params.schoolName = schoolName;
    if (className) params.className = className;
    if (durationBucket) params.durationBucket = durationBucket;
    const response = await api.get(
      `/admin/answers/questionnaire/${questionnaireId}`,
      { params },
    );
    return response.data.data;
  },

  /** 作废一份答卷（带可选原因）。返回更新后的答卷。 */
  invalidateAnswer: async (
    answerId: number,
    reason?: string,
  ): Promise<QuestionnaireAnswer> => {
    const response = await api.put(
      `/admin/answers/${answerId}/invalidate`,
      { reason: reason ?? null },
    );
    return response.data.data;
  },

  /** 恢复一份作废答卷为有效。返回更新后的答卷。 */
  restoreAnswer: async (answerId: number): Promise<QuestionnaireAnswer> => {
    const response = await api.put(`/admin/answers/${answerId}/restore`);
    return response.data.data;
  },
};
